// @flow

import React, { PureComponent } from 'react';
import qs from 'qs';
import debounce from 'lodash.debounce';
import { Map } from 'immutable';
import { graphql, QueryRenderer } from 'react-relay';
import { i18n, Analytics } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import AddUpdateGLAccountModal from 'main-app/components/AddUpdateGLAccountModal';
import FilterControls, {
  type FilterValue,
} from 'main-app/components/FilterControls';
import type { RouterHistory } from 'react-router';
import getSortOptions from './getSortOptions';
import getFilterOptions from './getFilterOptions';
import PaginatedGLAccountsContainer from './PaginatedGLAccountsContainer';

type Props = { history: RouterHistory };

type State = {
  modalOpen: boolean,
  editingAccount: ?Object,
  search: string,
  graphqlSearchValue: string,
};

const DEFAULT_FILTER = {
  sort: 'ACCOUNT_NUMBER',
  direction: 'ASC',
};

class ChartOfAccounts extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      modalOpen: false,
      editingAccount: null,
      search: '',
      graphqlSearchValue: '',
    };
    this.handleDebouncedSearch = debounce(this.handleDebouncedSearch, 500);
  }

  getFilters = (): FilterValue => {
    const { history } = this.props;
    const { search } = history.location;
    if (!search) return DEFAULT_FILTER;
    return {
      ...DEFAULT_FILTER,
      ...qs.parse(search, { ignoreQueryPrefix: true }),
    };
  };

  setFilters = (filters: Object) => {
    const { history } = this.props;
    history.replace(
      '/chart-of-accounts?' +
        qs.stringify(filters, { encode: true, arrayFormat: 'brackets' }),
    );
  };

  filtersDirty = () => {
    const { graphqlSearchValue } = this.state;
    return (
      !Map(this.getFilters()).equals(Map(DEFAULT_FILTER)) ||
      graphqlSearchValue
    );
  };

  compileQueryVariables = () => {
    const filters = this.getFilters();
    const { graphqlSearchValue } = this.state;
    const queryVariables: Object = {
      ...filters,
      search: graphqlSearchValue.trim() || null,
      sortBy: { field: filters.sort, direction: filters.direction },
    };

    if (queryVariables.isActive !== undefined) {
      queryVariables.isActive = queryVariables.isActive === 'true';
    }

    delete queryVariables.sort;
    delete queryVariables.direction;
    return queryVariables;
  };

  handleSearchInput = (search: string) => {
    this.setState({ search }, this.handleDebouncedSearch);
  };

  handleDebouncedSearch = () => {
    const { search } = this.state;
    this.setState({ graphqlSearchValue: search });
    Analytics.trackEvent('Search Applied', {
      listName: 'Chart of Accounts',
      searchString: search,
    });
  };

  handleOpenCreateModal = () => {
    this.setState({ modalOpen: true, editingAccount: null });
  };

  handleEditAccount = (account: Object) => {
    this.setState({ modalOpen: true, editingAccount: account });
  };

  handleCloseModal = () => {
    this.setState({ modalOpen: false, editingAccount: null });
  };

  render() {
    const { history } = this.props;
    const { search, modalOpen, editingAccount } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query ChartOfAccountsQuery(
            $count: Int!
            $cursor: String
            $search: String
            $sortBy: GLAccountSort
            $accountTypes: [GLAccountType!]
            $isActive: Boolean
          ) {
            ...PaginatedGLAccountsContainer_glAccounts
              @arguments(
                search: $search
                sortBy: $sortBy
                accountTypes: $accountTypes
                isActive: $isActive
              )
            viewer {
              id
              permissions
            }
          }
        `}
        variables={{ ...this.compileQueryVariables(), count: 30, cursor: null }}
        render={({ props }) => (
          <Page>
            <PageHeaderList
              title={i18n.t('Chart of Accounts')}
              createButtonLabel={props && i18n.t('New Account')}
              onCreateButtonClick={this.handleOpenCreateModal}
              searchPlaceholder={i18n.t(
                'Search by account number or name',
              )}
              searchValue={search}
              onSearchChange={this.handleSearchInput}
            />
            <FilterControls
              value={this.getFilters()}
              defaultValue={DEFAULT_FILTER}
              sortOptions={getSortOptions()}
              filterOptions={getFilterOptions()}
              onFiltersChange={filters => this.setFilters(filters)}
            />
            {!props ? (
              <Loader />
            ) : (
              <PaginatedGLAccountsContainer
                history={history}
                filtersDirty={this.filtersDirty()}
                onCreateNew={this.handleOpenCreateModal}
                onEdit={this.handleEditAccount}
                glAccounts={props}
              />
            )}
            {modalOpen && (
              <AddUpdateGLAccountModal
                onClose={this.handleCloseModal}
                account={editingAccount}
              />
            )}
          </Page>
        )}
      />
    );
  }
}

export default ChartOfAccounts;
